/**
 * @author      hugo@zumper.com (Hugo Pineda)
 * @copyright   Copyright (c) 2021, Zumper
 */
/** @jsx jsx */

import { jsx } from '@emotion/react'
import { selectIsMounted } from '@zcode-ui/web-zumper-global-state'
import { IS_BROWSER } from '@zcode/env'
import { Image, type ImageProps } from '@zcode/image'
import { buildSrc, buildSrcSet, sizeParams } from '@zcode/image-cdn-utils'
import { Size, getImagePlaceholder } from '@zcode/nr/es/shared/utils'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { PIXEL_RATIOS } from '../../helpers/pixelRatios'
import { image, noImage } from './styles'

interface Props
  extends Partial<
    ImageProps & Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'sizes' | 'srcSet' | 'src'>
  > {
  customImageHeight?: number
  customImageWidth?: number
  src?: string
  shouldPreloadImage?: boolean
  groupId?: number
}

const width = 329
const height = 200

export const ImageWrapper = ({
  customImageHeight,
  customImageWidth,
  src,
  shouldPreloadImage,
  groupId = 0,
  ...rest
}: Props) => {
  const appMounted = useSelector(selectIsMounted)
  const noImagePlaceholderUrl = useMemo(() => getImagePlaceholder(groupId, Size.small), [groupId])

  const imageWidth = customImageWidth ?? width
  const imageHeight = customImageHeight ?? height
  const ratio = imageHeight / imageWidth
  if (!src) {
    return <div css={noImage} style={{ backgroundImage: `url(${noImagePlaceholderUrl})` }} />
  }

  return (
    <Image
      alt=""
      css={image}
      height={imageHeight}
      // if we use `<img src="some-image.jpg" loading="lazy">`, the browser can decide
      // to load that image whenever it thinks is best. It might load them before other
      // assets that we think are more important. So we use `<img data-src="some-image.jpg"
      // loading="lazy">` to hide the image URL from the browser and prevent it from
      // loading the image until we allow it (by setting the `src` attribute)
      shouldUseSrc={rest.loading === 'eager' || (IS_BROWSER && appMounted)}
      src={buildSrc(src, sizeParams(imageWidth, ratio, { fit: 'crop' }))}
      srcSet={buildSrcSet(src, sizeParams(imageWidth, ratio, { fit: 'crop' }), PIXEL_RATIOS)}
      width={imageWidth}
      {...rest}
    />
  )
}
